import { SubHeader, SubHeaderTitle } from '@campoint/odi-ui';
import { Container, VStack, useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import usp4 from '../../assets/images/shared/payout.svg';
import usp1 from '../../assets/images/usp/usp1.svg';
import usp2 from '../../assets/images/usp/usp2.svg';
import usp3 from '../../assets/images/usp/usp3.svg';
import usp5 from '../../assets/images/usp/usp5Feed.svg';
import { FluentPageLayout } from '../../components/Layout/FluentPageLayout';
import { LayoutCenteringVStack } from '../../components/Layout/LayoutCenteringVStack';
import ScrollToLastKnownPositionOnMount from '../../components/Layout/ScrollToLastKnownPositionOnMount';
import PayoutDataCheckPendingCard from '../../components/shared/Payout/PayoutDataCheckPendingCard/PayoutDataCheckPendingCard';
import PayoutDataCheckRejectedCard from '../../components/shared/Payout/PayoutDataCheckRejectedCard/PayoutDataCheckRejectedCard';
import {
  USPModal,
  USPModalSteps,
} from '../../components/shared/USPModal/USPModal';
import { UpdateTaxInformationExpiredHintSlot } from '../../components/shared/UpdateTaxInformationExpiredHintSlot/UpdateTaxInformationExpiredHintSlot';
import { UpdateTaxInformationRejectedCardSlot } from '../../components/shared/UpdateTaxInformationRejectedCardSlot/UpdateTaxInformationRejectedCardSlot';
import {
  HelpcenterFaqEntry,
  ProfileStatusEnum,
  useGetHomeDataQuery,
} from '../../generated/graphql';
import { useAccountStatus } from '../../provider/AccountStatusProvider/AccountStatusProvider';
import { useAuth } from '../../provider/AuthProvider';
import { useFinanceService } from '../../provider/FinanceService/FinanceService';
import { useOnboardingTaskStatusContext } from '../../provider/OnboardingTaskStatusProvider';
import { useStatistic } from '../../provider/StatisticProvider';
import { useTour } from '../../provider/TourProvider/TourProvider';
import { extractDefinedEntries } from '../../utils/utils';
import { InfoPayoutWillBeBlockedAlert } from '../FinancePage/section/PayoutTaxSection/PayoutTaxSection';
import EmailNotVerifiedHint from './components/Home/EmailNotVerifiedHint/EmailNotVerifiedHint';
import { GoOnlineCard } from './components/Home/GoOnlineCard/GoOnlineCard';
import { SwitchVXMClassic } from './components/Home/SwitchVXMClassic/SwitchVXMClassic';
import { SwitchVXMClassicModal } from './components/Home/SwitchVXMClassic/SwitchVXMClassicModal';
import { HelpCenterSection } from './section/HelpCenterSection/HelpCenterSection';
import { IncomeSourceSection } from './section/IncomeSourceSection/IncomeSourceSection';
import StatisticSection from './section/StatisticSection/StatisticSection';
import { IncomeStatsTile } from './section/StatisticSection/StatsTile/StatsTile';
import TaskSection, {
  TaskSectionProps,
} from './section/TaskSection/TaskSection';
import { TipSection } from './section/TipSection/TipSection';

type DisplayData = {
  faqEntries: HelpcenterFaqEntry[];
  payableAmount: number;
};

type VisibilitySwitches = {
  emailNotVerifiedHint: boolean;
  faq: boolean;
  goOnlineCard: boolean;
  incomeCard: boolean;
  taskCard: boolean;
  statisticsSection: boolean;
};

const steps: TaskSectionProps['steps'] = {
  account: 'done',
  age: 'todo',
  profile: 'todo',
  modelReleaseForm: 'done',
};

const Home: React.FC = () => {
  const authCtx = useAuth();
  const {
    loading: isFinanceServiceLoading,
    isFirstPayout,
    payableAmount,
    hasEnoughAmountForPayout,
    payoutWizardStatus,
    action: { triggerRefetch: triggerFinanceServiceRefetch },
  } = useFinanceService();
  const {
    isAccountStatusLoading,
    hasAllOnboardingTasksCompleted,
    verificationTourStatus,
    profileTourStatus,
    mrfTourStatus,
    isAllowedToGoOnlineOnVXLive,
    isAllowedToGoOnlineOnVXLiveButWasNotYet,
    wasOnlineOnVXLive,
    actions: { refresh: triggerAccountStatusRefetch },
  } = useAccountStatus();
  const { t } = useTranslation(['home', 'general', 'usp', 'payout']);
  const history = useHistory();
  const { search } = useLocation();

  // load data from backend
  const { data, loading: isDataLoading, refetch } = useGetHomeDataQuery();

  const { lastUpdatedAt } = useStatistic(isAllowedToGoOnlineOnVXLive);

  const isContentLoading =
    isDataLoading ||
    isAccountStatusLoading ||
    isFinanceServiceLoading ||
    (lastUpdatedAt === null && isAllowedToGoOnlineOnVXLive);

  const { isEmailVerified, differences: onboardingStatusDifferences } =
    useOnboardingTaskStatusContext();

  const {
    isOpen: isUspModalOpen,
    onOpen: openUspModal,
    onClose: closeUspModal,
  } = useDisclosure();

  // Handle first login and the usps
  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const isFirstLoginQueryParamKey = 'isFirstLogin';

    if (!queryParams.get(isFirstLoginQueryParamKey)) {
      return;
    }

    openUspModal();
  }, [history, openUspModal, search]);

  useEffect(() => {
    if (onboardingStatusDifferences.length > 0) {
      triggerAccountStatusRefetch();
      refetch().then();
    }
  }, [onboardingStatusDifferences, refetch, triggerAccountStatusRefetch]);

  useEffect(() => {
    triggerAccountStatusRefetch();
    triggerFinanceServiceRefetch();
  }, [triggerFinanceServiceRefetch, triggerAccountStatusRefetch]);

  steps.age = verificationTourStatus;
  steps.profile = profileTourStatus;
  steps.modelReleaseForm = mrfTourStatus;

  const [switchModalOpen, setSwitchModalOpen] = useState(false);

  const tourCtx = useTour();

  useEffect(() => {
    if (wasOnlineOnVXLive) {
      return;
    }
    if (hasAllOnboardingTasksCompleted) {
      tourCtx.actions.startWelcomeTour({ skipIfAlreadFinishedOnce: true });
    }
  }, [tourCtx, wasOnlineOnVXLive, hasAllOnboardingTasksCompleted]);

  // Initialize display data
  const displayData: DisplayData = {
    faqEntries: extractDefinedEntries(data?.helpcenter?.faq?.entries),
    payableAmount: data?.finances.payoutStatus.payableAmount ?? 0,
  };

  const mappedEmailData = React.useMemo(() => {
    const email = data?.account.contact.currentEmailAddress ?? '';
    const isNewEmailVerified =
      data?.account.contact.isCurrentEmailAddressVerified ?? false;

    return {
      email,
      isNewEmailVerified,
    };
  }, [data]);

  const visibilitySwitches: VisibilitySwitches = {
    emailNotVerifiedHint: !mappedEmailData.isNewEmailVerified,
    faq: isFirstPayout || !hasEnoughAmountForPayout,
    incomeCard: payableAmount > 0,
    taskCard: !isAllowedToGoOnlineOnVXLive,
    goOnlineCard: isAllowedToGoOnlineOnVXLiveButWasNotYet,
    statisticsSection: isAllowedToGoOnlineOnVXLive,
  };

  const canSeeClassicLink =
    !visibilitySwitches.emailNotVerifiedHint &&
    !visibilitySwitches.taskCard &&
    visibilitySwitches.statisticsSection;

  const incomeCardHeadline = isFirstPayout
    ? t('home:cards.incomeCard.headline1')
    : t('home:cards.incomeCard.headline2');

  const uspSteps: USPModalSteps[] = [
    {
      image: {
        src: usp1,
        alt: t('usp:img.ModelPosiertMitWehendemSuperheldenCape'),
      },
      heading: t('usp:heading.WillkommenBeiVXModels'),
      text: t('usp:text.UndHerzlichenGluckwunschAbHeuteBistDuDeinEigenerBoss'),
    },
    {
      image: {
        src: usp2,
        alt: t('usp:img.ModelStehtVergleichendNebenIhremModelprofil'),
      },
      heading: t('usp:heading.VerwalteDeinModelprofil'),
      text: t(
        'usp:text.PflegeDeinVISITXModelprofilUmEinenKundenstammAufzubauen'
      ),
    },
    {
      image: {
        src: usp3,
        alt: t('usp:img.ModelChattetMitKunde'),
      },
      heading: t('usp:heading.GeheVonUberallOnlineUndVerdieneDeinGeld'),
      text: t(
        'usp:text.EgalWoDuGeradeBistChatteOderCameMitDeinenKundenVonUberall'
      ),
    },
    {
      image: {
        src: usp4,
        alt: t('usp:img.DiagrammSteigenderEinnahmen'),
      },
      heading: t('usp:heading.BehalteDenUberblickUberDeinenVerdienst'),
      text: t('usp:text.CheckeDeinenVerdienstRundUmDieUhr'),
    },
    {
      image: {
        src: usp5,
        alt: t('usp:img.FeedBildAlt'),
      },
      heading: t('usp:heading.TeileDieSchoenstenMomenteDeinesTages'),
      text: t('usp:text.FeedErstelleBeitraegeMitBildernoderClipsUndHalteDeine'),
    },
  ];

  const spacing = { base: 4, lg: 8 };
  return (
    <>
      <USPModal
        isOpen={isUspModalOpen}
        onClose={closeUspModal}
        closeButtonText={t('usp:button.CoolAllesKlar')}
        continueButtonText={t('general:button.Weiter')}
        steps={uspSteps}
      />

      <FluentPageLayout isContentLoading={isContentLoading}>
        <ScrollToLastKnownPositionOnMount identifier={'/home'} />
        <SubHeader>
          <Container px={0} maxW={'container.xl'}>
            <SubHeaderTitle fontSize={'xl'}>
              {t('home:headline.WillkommenbeiVXModels')}
            </SubHeaderTitle>
          </Container>
        </SubHeader>
        <Container maxW="container.xl" p={0}>
          <VStack spacing={spacing} w={'full'} mb={spacing} mt={spacing}>
            {canSeeClassicLink && (
              <SwitchVXMClassic onClick={() => setSwitchModalOpen(true)} />
            )}
            {visibilitySwitches.emailNotVerifiedHint && (
              <LayoutCenteringVStack>
                <EmailNotVerifiedHint email={mappedEmailData.email} />
              </LayoutCenteringVStack>
            )}

            <InfoPayoutWillBeBlockedAlert />
            <UpdateTaxInformationExpiredHintSlot />
            <UpdateTaxInformationRejectedCardSlot />

            {authCtx.isMasterAccount && visibilitySwitches.incomeCard && (
              <LayoutCenteringVStack>
                <IncomeStatsTile
                  headline={incomeCardHeadline}
                  payableAmount={displayData.payableAmount}
                  showIncomeIndicator={false}
                />
              </LayoutCenteringVStack>
            )}

            {authCtx.isMasterAccount &&
              payoutWizardStatus === ProfileStatusEnum.Pending && (
                <LayoutCenteringVStack>
                  <PayoutDataCheckPendingCard />
                </LayoutCenteringVStack>
              )}
            {authCtx.isMasterAccount &&
              payoutWizardStatus === ProfileStatusEnum.Rejected && (
                <LayoutCenteringVStack>
                  <PayoutDataCheckRejectedCard reason={''} />
                </LayoutCenteringVStack>
              )}

            {visibilitySwitches.taskCard && (
              <TaskSection
                isAccountEmailVerified={isEmailVerified}
                steps={steps}
              />
            )}

            {visibilitySwitches.goOnlineCard && (
              <LayoutCenteringVStack>
                <GoOnlineCard />
              </LayoutCenteringVStack>
            )}

            {/* The statistics section should only be shown if the model is ready to go only
            (-> has finished all onboarding tasks) */}
            {visibilitySwitches.statisticsSection && <StatisticSection />}

            <TipSection />

            <IncomeSourceSection />

            {visibilitySwitches.faq && (
              <HelpCenterSection entries={displayData.faqEntries} />
            )}
          </VStack>
        </Container>
        <SwitchVXMClassicModal
          isOpen={switchModalOpen}
          onClose={() => setSwitchModalOpen(false)}
        />
      </FluentPageLayout>
    </>
  );
};

export default Home;
